import axios, {type AxiosResponse} from 'axios';
import store from '../store';
import LocalStorage from './localStorage';

let initialized = false;

type XHRManagerAddOptions = {
    ignoreLocalStorage: boolean
    send: boolean
}

type XHRManagerIntervalCallback = {
    options: XHRManagerAddOptions
    callback: XHRCallback
};

type XHRManagerIntervalCallbacks = {
    [minutes: string]: {
        [methodName: string]: XHRManagerIntervalCallback[]
    }
};

const XHRManager = new class
{
    private intervalCallbacks: XHRManagerIntervalCallbacks = {};

    private intervals = {};

    private lastUpdates = {};

    private promises: Record<string, Promise<AxiosResponse>> = {};

    private sendTimeout: Nullable<number> = null;

    private sendQueue: {[methodName: string]: XHRCallback[]} = {};

    public sendCallback(methods: string | string[], callback: XHRCallback): void
    {
        this.send(methods, typeof methods === 'string' ? [methods] : methods)
        .then(({data: {data}}) =>
        {
            if (typeof methods === 'string')
            {
                callback(null, data[methods]);
            }
            else
            {
                callback(null, data);
            }
        })
        .catch((err) => callback(err));
    }

    private send(methods: string | string[], updateLocalStorage: string[]): Promise<AxiosResponse>
    {
        if (typeof methods === 'string')
        {
            methods = [methods];
        }

        if (methods.length === 0)
        {
            return Promise.reject(new Error('empty methods'));
        }

        let promiseKeys: string[] = [];
        for (let key of methods)
        {
            if('undefined' === typeof this.promises[key])
            {
                promiseKeys.push(key);
            }
        }

        methods = promiseKeys;

        if (methods.length === 0)
        {
            return Promise.reject(new Error('methods already requested'));
        }

        let promise = axios.post('/application/ajax/xhr', {keys: methods});

        for (let key of methods)
        {
            this.promises[key] = promise;
        }

        promise.finally(() =>
        {
            for (let key of methods)
            {
                if (updateLocalStorage.includes(key))
                {
                    this.lastUpdates[key] = Date.now();
                }

                delete this.promises[key];
            }

            if (updateLocalStorage.length)
            {
                // update local storage on finally
                LocalStorage.set('xhrManager', this.lastUpdates);
            }
        });

        return promise;
    }

    public add(minutes: number, method: string, options: Partial<XHRManagerAddOptions> | XHRCallback, callback?: XHRCallback): void
    {
        let _options: XHRManagerAddOptions = {
            ignoreLocalStorage: false,
            send: false,
        };
        let _callback: XHRCallback;
        if (typeof options === 'function')
        {
            _callback = options;
        }
        else if (typeof callback === 'function')
        {
            _callback = callback;
            if (options && typeof options === 'object')
            {
                _options = Object.assign({}, _options, options);
            }
            else if (options !== null)
            {
                throw new Error('invalid options');
            }
        }
        else
        {
            throw new Error('no callback given');
        }

        if (!initialized)
        {
            LocalStorage.on('xhrManager', () =>
            {
                let data = LocalStorage.get('xhrManager');
                if (typeof data === 'object' && data)
                {
                    this.lastUpdates = data;
                }
            });
        }

        minutes = Math.trunc(minutes);
        if (minutes <= 0)
        {
            throw new Error('invalid minutes, required more then 0 minutes');
        }

        // set object key minutes if no one exists
        if (typeof this.intervals[minutes] === 'undefined')
        {
            this.intervalCallbacks[minutes] = {};

            let updateInterval      = minutes * 60_000;
            this.intervals[minutes] = window.setInterval(() =>
            {
                if (!store.state.loggedIn)
                {
                    return;
                }

                let keys: string[]                     = [];
                let updateLocalStorage: string[]       = [];
                let tmpKeys                            = Object.keys(this.intervalCallbacks[minutes]);

                for (let key of tmpKeys)
                {
                    let ignoreLocalStorage = false;
                    for (let {options} of this.intervalCallbacks[minutes][key])
                    {
                        if (options.ignoreLocalStorage)
                        {
                            ignoreLocalStorage = true;
                            break;
                        }
                    }

                    let requireLocalStorageUpdate = typeof this.lastUpdates[key] === 'undefined' ||
                        Math.round((Date.now() - this.lastUpdates[key]) / 10_000) * 10_000 >= updateInterval;
                    if (ignoreLocalStorage || requireLocalStorageUpdate)
                    {
                        keys.push(key);
                    }
                    if (requireLocalStorageUpdate)
                    {
                        updateLocalStorage.push(key);
                    }
                }

                console.debug('[XHRManager]', keys, updateLocalStorage);
                if (keys.length === 0)
                {
                    return;
                }

                const runCallbacks = (method: string, err: object | null, data?: any) =>
                {
                    for (let {callback, options} of this.intervalCallbacks[minutes][method])
                    {
                        if (updateLocalStorage.includes(method) && !options.ignoreLocalStorage)
                        {
                            callback(err, data);
                        }
                        else if (keys.includes(method) && options.ignoreLocalStorage)
                        {
                            callback(err, data);
                        }
                    }
                };

                // check keys for update local storage
                this.send(keys, updateLocalStorage).then(({data: {data}}) =>
                {
                    for (let method in data)
                    {
                        if (data.hasOwnProperty(method) && typeof this.intervalCallbacks[minutes] !== 'undefined' && typeof this.intervalCallbacks[minutes][method] !== 'undefined')
                        {
                            runCallbacks(method, null, data[method]);
                        }
                    }
                }).catch((err) =>
                {
                    for (let method in this.intervalCallbacks[minutes])
                    {
                        if (this.intervalCallbacks[minutes].hasOwnProperty(method))
                        {
                            runCallbacks(method, err);
                        }
                    }
                });
            }, updateInterval);
        }

        // create callback array
        if (typeof this.intervalCallbacks[minutes][method] === 'undefined')
        {
            this.intervalCallbacks[minutes][method] = [];
        }

        let found = false;
        for (let {callback} of this.intervalCallbacks[minutes][method])
        {
            if (callback === _callback)
            {
                found = true;
                break;
            }
        }

        if (!found)
        {
            this.intervalCallbacks[minutes][method].push({
                options: _options,
                callback: _callback,
            });
        }

        // send directly -> merge within 100ms
        if (_options.send)
        {
            if (typeof this.sendQueue[method] === 'undefined')
            {
                this.sendQueue[method] = [];
            }

            this.sendQueue[method].push(_callback);
            this.sendTimeout && window.clearTimeout(this.sendTimeout);

            // merge direct send-option callbacks within max 100ms delay between execution - extend on further additions
            this.sendTimeout = window.setTimeout(() => {
                let queue = {};

                // copy callbacks for local handling
                for (const method in this.sendQueue)
                {
                    if (!this.sendQueue.hasOwnProperty(method))
                    {
                        continue;
                    }

                    queue[method] = [];

                    for (const callback of this.sendQueue[method])
                    {
                        queue[method].push(callback);
                    }
                }

                // reset queue
                this.sendQueue = {};

                this.sendCallback(Object.keys(queue), (error, methodResponses: {[method: string]: any}) => {
                    for (const method in methodResponses)
                    {
                        if (!methodResponses.hasOwnProperty(method))
                        {
                            continue;
                        }

                        for (const sendCallback of queue[method])
                        {
                            sendCallback(error, methodResponses[method]);
                        }
                    }
                });
            }, 100);
        }
    }

    public remove(minutes: number, method: string, removeCallback: XHRCallback): boolean
    {
        minutes = Math.trunc(minutes);

        // check if a callback exists
        if (typeof this.intervalCallbacks[minutes] === 'undefined' || typeof this.intervalCallbacks[minutes][method] === 'undefined')
        {
            return false;
        }

        let callbacks = this.intervalCallbacks[minutes][method];

        // check if given callback exists in interval
        let index = -1;
        for (let currentIndex in callbacks)
        {
            if (callbacks.hasOwnProperty(currentIndex))
            {
                if (callbacks[currentIndex].callback === removeCallback)
                {
                    index = parseInt(currentIndex);
                    break;
                }
            }
        }

        // no callback found
        if (index === -1)
        {
            return false;
        }

        callbacks.splice(index, 1);

        // if no other callback for minutes and method exists then remove method from interval and clear method callbacks
        if (callbacks.length === 0)
        {
            // clear callbacks for given method
            delete this.intervalCallbacks[minutes][method];
        }

        // if no another interval method exists then clear interval and delete all variables
        if (Object.keys(this.intervalCallbacks[minutes]).length === 0)
        {
            window.clearInterval(this.intervals[minutes]);

            delete this.intervalCallbacks[minutes];
            delete this.intervals[minutes];
        }

        return true;
    }
};

export default XHRManager;